import { useState, useEffect } from "react";
import "./UploadProgress.css";

export default function UploadProgress({ 
  progress = 0, 
  isProcessing = false, 
  processingProgress = 0 
}) {
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState(null);
  const [startTime, setStartTime] = useState(Date.now());
  const [prevProgress, setPrevProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);

  useEffect(() => {
    // Reset start time when progress is reset to 0
    if (progress === 0 && prevProgress > 0) {
      setStartTime(Date.now());
    }
    
    // Calculate upload speed and estimated time left
    if (progress > prevProgress && progress < 100 && !isProcessing) {
      const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
      const progressDiff = progress - prevProgress;
      const timePerPercent = elapsedTime / progress;
      const remainingPercent = 100 - progress;
      const estimatedSeconds = timePerPercent * remainingPercent;
      
      // Only update if we have reasonable values
      if (elapsedTime > 0 && progress > 5) {
        setEstimatedTimeLeft(Math.round(estimatedSeconds));
        
        // Calculate upload speed (very rough estimate)
        const speedMbps = (progress / elapsedTime) * 0.1; // Arbitrary scaling factor
        setUploadSpeed(speedMbps.toFixed(1));
      }
    }
    
    setPrevProgress(progress);
  }, [progress, prevProgress, isProcessing, startTime]);

  const circumference = 2 * Math.PI * 45;
  const displayProgress = isProcessing ? processingProgress : progress;
  const strokeDashoffset = circumference - (displayProgress / 100) * circumference;

  // Format estimated time left
  const formatTimeLeft = (seconds) => {
    if (!seconds) return '';
    
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="upload-progress">
      <div className="progress-circle">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle
            className="progress-background"
            cx="60"
            cy="60"
            r="45"
            fill="none"
            strokeWidth="8"
          />
          <circle
            className="progress-indicator"
            cx="60"
            cy="60"
            r="45"
            fill="none"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <span className="progress-text">{displayProgress}%</span>
      </div>
      {isProcessing ? (
        <>
          <h2 className="progress-title">Processing Your Recording...</h2>
          <p className="progress-subtitle">
            Your file has been uploaded successfully. We're now processing it to generate the transcript.
            {processingProgress < 95 && " This may take several minutes for longer recordings."}
          </p>
        </>
      ) : (
        <>
          <h2 className="progress-title">Uploading Your Recording...</h2>
          <p className="progress-subtitle">
            Please wait while we securely upload your recording.
            {progress > 0 && progress < 100 && estimatedTimeLeft && (
              <span> Estimated time remaining: {formatTimeLeft(estimatedTimeLeft)}</span>
            )}
            {progress > 0 && progress < 100 && uploadSpeed > 0 && (
              <span> (Upload speed: ~{uploadSpeed} Mbps)</span>
            )}
          </p>
        </>
      )}
    </div>
  );
}
